import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

// Layout
import { Header } from "./components/layout/Header";
import { Footer } from "./components/layout/Footer";

// Public Pages
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import StoriesPage from "./pages/StoriesPage";
import StoryDetailPage from "./pages/StoryDetailPage";
import AuthorsPage from "./pages/AuthorsPage";
import AuthorDetailPage from "./pages/AuthorDetailPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";

// Protected Pages
import AccountPage from "./pages/AccountPage";
import BookmarksPage from "./pages/BookmarksPage";

// Admin Pages
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminStories from "./pages/admin/AdminStories";
import AdminAuthors from "./pages/admin/AdminAuthors";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminPromoCodes from "./pages/admin/AdminPromoCodes";
import AdminAuditLog from "./pages/admin/AdminAuditLog";

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
    const { user, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-pulse text-muted-foreground">Načítání...</div>
            </div>
        );
    }
    
    if (!user) {
        return <Navigate to="/prihlaseni" replace />;
    }
    
    return children;
};

// Public layout wrapper
const PublicLayout = ({ children }) => {
    return (
        <>
            <Header />
            <main className="min-h-screen">
                {children}
            </main>
            <Footer />
        </>
    );
};

function AppRoutes() {
    return (
        <Routes>
            {/* Public routes */}
            <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
            <Route path="/o-projektu" element={<PublicLayout><AboutPage /></PublicLayout>} />
            <Route path="/povidky" element={<PublicLayout><StoriesPage /></PublicLayout>} />
            <Route path="/povidka/:id" element={<PublicLayout><StoryDetailPage /></PublicLayout>} />
            <Route path="/autori" element={<PublicLayout><AuthorsPage /></PublicLayout>} />
            <Route path="/autor/:id" element={<PublicLayout><AuthorDetailPage /></PublicLayout>} />
            <Route path="/prihlaseni" element={<PublicLayout><LoginPage /></PublicLayout>} />
            <Route path="/registrace" element={<PublicLayout><RegisterPage /></PublicLayout>} />
            
            {/* Protected routes */}
            <Route path="/ucet" element={
                <ProtectedRoute>
                    <PublicLayout><AccountPage /></PublicLayout>
                </ProtectedRoute>
            } />
            <Route path="/ulozene" element={
                <ProtectedRoute>
                    <PublicLayout><BookmarksPage /></PublicLayout>
                </ProtectedRoute>
            } />
            
            {/* Admin routes */}
            <Route path="/admin" element={
                <ProtectedRoute>
                    <AdminLayout />
                </ProtectedRoute>
            }>
                <Route index element={<AdminDashboard />} />
                <Route path="povidky" element={<AdminStories />} />
                <Route path="autori" element={<AdminAuthors />} />
                <Route path="uzivatele" element={<AdminUsers />} />
                <Route path="promo-kody" element={<AdminPromoCodes />} />
                <Route path="audit-log" element={<AdminAuditLog />} />
            </Route>
            
            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <AppRoutes />
                <Toaster position="bottom-right" />
            </BrowserRouter>
        </AuthProvider>
    );
}

export default App;
